import type { ReactElement } from 'react'
import type { ScalarsType } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import type { useMeetingResultsCubit } from 'hooks/cubits/meetingsResults'
import { useBreakoutUser } from 'hooks/profile'
import { useTranslationTyped } from 'i18n/i18n'
import {
  GradingTooltipAttendance,
  GradingTooltipCompletion,
  GradingTooltipCriticalThinking,
  GradingToolTipQuizScore,
} from 'pages/instructor/shared/GradingTooltips'
import { toFinalScorePercent } from '@breakoutlearning/firebase-repository/sources-of-truth/percentage'

// Derived from the assignment's grading scalars so the four keys have exactly
// one definition. Adding a scalar widens this automatically.
export type ScoreComponentKey = keyof Exclude<ScalarsType, undefined>

export type ScoreComponent = {
  key: ScoreComponentKey
  label: string
  /** display score, integer 0–100; null when the deck has no scorable questions */
  score: number | null
  /** the same score as a 0–1 float, for total arithmetic */
  raw: number | null
  /** display weight, integer 0–100 */
  weight: number
  /** the same weight as a 0–1 float */
  rawWeight: number
  isAI: boolean
  tooltip: ReactElement
}

/**
 * The four graded components for the viewed user, in display order. Returns null
 * until the assignment, its weights, and the user's scores are all available.
 *
 * Consumers decide what to do with a zero-weight component: the score breakdown
 * filters them out, the appeal form greys them out.
 */
export function useScoreComponents(
  cubit: ReturnType<typeof useMeetingResultsCubit>
): ScoreComponent[] | null {
  const { tt } = useTranslationTyped()
  const viewer = useBreakoutUser()
  // Faculty and Corre staff get the instructor-worded Contribution tooltip;
  // participants get the participant wording.
  const isInstructorView = viewer.isFaculty || viewer.isCorre

  const assignment = cubit.assignment
  const userScoreData = cubit.scalarRawScoreDataForUser

  if (
    !assignment.isLoaded ||
    !assignment.data.assignmentGradingScalars ||
    !userScoreData
  ) {
    return null
  }

  const scalars = assignment.data.assignmentGradingScalars

  // quizScore is null when there are no scoring questions (only sorting ones)
  const rawQuiz = userScoreData.quizScore ?? null
  const rawContribution = userScoreData.totalRubricScore || 0

  return [
    {
      key: 'quizScore',
      label: tt.meeting.results.scalar_labels.chart_legends.quizScore(),
      score: rawQuiz !== null ? Math.round(rawQuiz * 100) : null,
      raw: rawQuiz,
      weight: Math.round(scalars.quizScore * 100),
      rawWeight: scalars.quizScore,
      isAI: false,
      tooltip: <GradingToolTipQuizScore />,
    },
    {
      key: 'attendanceScore',
      label: tt.meeting.results.scalar_labels.chart_legends.attendanceScore(),
      score: Math.round(userScoreData.attendanceScore * 100),
      raw: userScoreData.attendanceScore,
      weight: Math.round(scalars.attendanceScore * 100),
      rawWeight: scalars.attendanceScore,
      isAI: false,
      tooltip: <GradingTooltipAttendance />,
    },
    {
      key: 'participationScore',
      label:
        tt.meeting.results.scalar_labels.chart_legends.participationScore(),
      score: Math.round(userScoreData.participationScore * 100),
      raw: userScoreData.participationScore,
      weight: Math.round(scalars.participationScore * 100),
      rawWeight: scalars.participationScore,
      isAI: false,
      tooltip: (
        <GradingTooltipCompletion metric={assignment.data.completionMetric} />
      ),
    },
    {
      key: 'totalRubricScore',
      label: tt.meeting.results.scalar_labels.chart_legends.totalRubricScore(),
      score: Math.round(rawContribution * 100),
      raw: rawContribution,
      weight: Math.round(scalars.totalRubricScore * 100),
      rawWeight: scalars.totalRubricScore,
      isAI: true,
      tooltip: (
        <GradingTooltipCriticalThinking
          audience={isInstructorView ? 'instructor' : 'participant'}
        />
      ),
    },
  ]
}

/**
 * The final score these components produce, as an integer percentage.
 *
 * `proposedByKey` overrides a component's score with a proposed percentage;
 * anything absent keeps the score the student actually has. Mirrors
 * `calculateScalarRawScoreDataForUser` — a null quiz contributes nothing rather
 * than counting as zero — and rounds like `MeetingResultsCubit.finalScoreData`,
 * so `projectedFinalScore(components, {})` equals the displayed final score.
 */
export function projectedFinalScore(
  components: ScoreComponent[],
  proposedByKey: Partial<Record<ScoreComponentKey, number>> = {}
): number {
  const total = components.reduce((sum, component) => {
    const proposed = proposedByKey[component.key]
    const raw = proposed !== undefined ? proposed / 100 : component.raw
    if (raw === null) return sum
    return sum + raw * component.rawWeight
  }, 0)

  return toFinalScorePercent(total)
}

export function weightedComponentScore(
  component: ScoreComponent
): number | null {
  if (component.raw === null) return null
  return Math.round(component.raw * component.rawWeight * 100)
}
